import { Col, Row, Form, Button } from "react-bootstrap";
import { ClaimAlertState } from "../../claims/types";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { convertUnixMilisegToFormateDate } from "../../../utils/dateUtils";
import { updageAlertClaim } from "../api/updateAlertClaim";
import { useAlert } from "../../../hooks/useAlert";
import { useUsersSelect } from "../../../hooks/getUsersByClient";
import { Topic } from "../../topics/types";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import SentimentIndicator from "../../../components/SentimentIndicatorSelector";
import GenerateAlertSelector from "../../../components/GenerateAlertSelctor";
import { useCurrentClient } from "../../auth/api/getCurrentClient";
import AlertDetailWrapper from "./AlertDetailWrapper";
import TopicsSection from "../../topics/components/TopicsSection";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { PERMISSIONS } from "../../../constants/permissions";

interface AlertClaimDetailProps {
    claim: ClaimAlertState;
    onGoBackClick: (isUpdated: boolean) => void;
}

export default function AlertClaimDetail({ claim, onGoBackClick }: AlertClaimDetailProps) {
    const navigate = useNavigate();

    const { t } = useTranslation(["common", "claim"]);
    const queryClient = useQueryClient();

    const [isUpdated, setIsUpdated] = useState(false);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [topics, setTopics] = useState<Topic[]>([]);
    const [updateClaim, setUpdateClaim] = useState(claim);

    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: true });
    const { data: users } = useUsersSelect({});
    const { data: currentClient } = useCurrentClient();
    
    const currentUserPermissions = useSelector((state: RootState) => state.currentUserInfo.data.permissions);

    useEffect(() => {
        setTopics(claim.topics || []);
    }, [claim.topics]);

    const handleSave = async () => {
        setLoading(true);
        const payload = { ...updateClaim, topics };

        try {
            await updageAlertClaim(claim.idClaim, payload);
            setEditing(false);
            setIsUpdated(true);
            showAlert({
                message: t("claim:alertDetail.showDetailClaimUpdateSuccesfully"),
                type: "success",
                duration: 8000,
            });
            await queryClient.invalidateQueries({
                queryKey: ["alertClaim", claim.idClaim],
            });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError.message,
                type: "danger",
                duration: 8000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setEditing(false);
        onGoBackClick(isUpdated);
    };

    return (
        <AlertDetailWrapper
            loading={loading}
            editing={editing}
            isVisible={isVisible}
            alert={alert}
            onHideAlert={hideAlert}
            onEditClick={() => setEditing(true)}
            onBackClick={handleBack}
            onSaveClick={handleSave}
        >
            <Row>
                <Col xs={6} md={6}>
                    <Form.Group>
                        <Form.Label>{t("claim:alertDetail.showDetailClaimIdClaim")}</Form.Label>
                        <Form.Control type="text" value={claim.idClaim} disabled />
                    </Form.Group>
                </Col>
                <Col xs={6} sm={6} md={6}>
                    <Form.Label>{t("claim:alertDetail.showDetailClaimUserRelatedSelect")}</Form.Label>
                    <Form.Select
                        className="form-control"
                        value={updateClaim.idUserRelated}
                        disabled={!editing}
                        onChange={(e) =>
                            setUpdateClaim({
                                ...updateClaim,
                                idUserRelated: e.target.value,
                            })
                        }
                    >
                        <option value=""></option>
                        {users?.map((user) => (
                            <option key={user.id} value={user.id}>
                                {user.name}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            <Row>
                <Col xs={6} md={6}>
                    <Form.Group>
                        <Form.Label>{t("claim:alertDetail.showDetailClaimDateClaim")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={convertUnixMilisegToFormateDate(claim.entryDate?.toString() ?? "")}
                            disabled
                        />
                    </Form.Group>
                </Col>
                <Col xs={6} md={6}>
                    <Form.Group>
                        <Form.Label>{t("claim:alertDetail.showDetailClaimNameAffected")}</Form.Label>
                        <Form.Control type="text" value={claim.nameAndSurnameAffected} disabled />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col xs={6} md={6}>
                    <Form.Group>
                        <Form.Label>{t("claim:alertDetail.showDetailClaimFeelingAI")}</Form.Label>
                        <SentimentIndicator
                            value={updateClaim.feelingAI ?? null}
                            disabled={true}
                            showSentimentLabel={true}
                            minValue={currentClient?.sentimentMinimumValue}
                            maxValue={currentClient?.sentimentMaximumValue}
                            onChange={(newValue) => {
                                if (newValue == null) return;
                                setUpdateClaim({ ...updateClaim, feelingAI: newValue });
                            }}
                        />
                        {updateClaim.originalFeelingAI != null &&
                            updateClaim.feelingAI !== updateClaim.originalFeelingAI && (
                                <p style={{ color: "green", marginTop: "10px" }}>
                                    {t("claim:alertDetail.showDetailClaimyAISentimentGenerated")}{" "}
                                    {updateClaim.originalFeelingAI}
                                </p>
                            )}
                    </Form.Group>
                </Col>
                <Col xs={6} md={6}>
                    <Form.Group>
                        <Form.Label>{t("claim:alertDetail.showDetailClaimAlertAI")}</Form.Label>
                        <GenerateAlertSelector
                            value={updateClaim.alertAI ?? null}
                            disabled={true}
                            minValue={currentClient?.sentimentMinimumValue}
                            maxValue={currentClient?.sentimentMaximumValue}
                            neutralReference={currentClient?.neutralReference}
                            alertMinimumValue={currentClient?.alertMinimumValue}
                            alertMaximumValue={currentClient?.alertMaximumValue}
                            onChange={(newValue) => {
                                if (newValue == null) return;
                                setUpdateClaim({ ...updateClaim, alertAI: newValue });
                            }}
                        />
                        {updateClaim.originalAlertAI != null &&
                            updateClaim.alertAI !== updateClaim.originalAlertAI && (
                                <p style={{ color: "green", marginTop: "10px" }}>
                                    {t("claim:alertDetail.showDetailClaimyAISentimentGenerated")}{" "}
                                    {updateClaim.originalAlertAI}
                                </p>
                            )}
                    </Form.Group>
                </Col>
            </Row>

            <TopicsSection
                topics={topics}
                setTopics={setTopics}
                editing={false}
                newTopicLabel={t("claim:alertDetail.showDetailClaimTopicNewTopic")}                
            />

            <Row>
                <Col xs={12} md={12}>
                    <Form.Group>
                        <Form.Label>{t("claim:alertDetail.showDetailClaimComment")}</Form.Label>
                        <Form.Control as="textarea" rows={5} value={updateClaim.comment} disabled />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col xs={12} md={12}>
                    <Form.Group>
                        <div className="mt-3">
                            <Form.Label>{t("claim:alertDetail.showDetailClaimRecommendationAI")}</Form.Label>
                            
                            {currentUserPermissions.length > 0 && (currentUserPermissions.includes(PERMISSIONS.CLAIMS.VIEW) || currentUserPermissions.includes(PERMISSIONS.CLAIMS.EDIT)) && (
                                <Button variant="secondary" className="float-end" onClick={() => navigate(`/edit-claim/${updateClaim.idClaim}/data?from=alert&mode=view`)}>{t("claim:alertDetail.viewRequest")}</Button>
                            )}
                        </div>
                        <Form.Control
                            as="textarea"
                            rows={5}
                            value={updateClaim.recommendationAI}
                            disabled
                            onChange={(e) =>
                                setUpdateClaim({
                                    ...updateClaim,
                                    recommendationAI: e.target.value,
                                })
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col xs={4} md={4}>
                    <Form.Group>
                        <Form.Label>{t("claim:alertDetail.showDetailClaimPhone")}</Form.Label>
                        <Form.Control type="text" value={claim.mobile} disabled />
                    </Form.Group>
                </Col>
                <Col xs={4} md={4}>
                    <Form.Group>
                        <Form.Label>{t("claim:alertDetail.showDetailClaimEmail")}</Form.Label>
                        <Form.Control type="text" value={claim.email} disabled />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col xs={12} md={12}>
                    <Form.Group>
                        <Form.Label>{t("claim:alertDetail.showDetailClaimMessageAI")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={5}
                            value={updateClaim.messageAI}
                            disabled
                            onChange={(e) =>
                                setUpdateClaim({
                                    ...updateClaim,
                                    messageAI: e.target.value,
                                })
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>
        </AlertDetailWrapper>
    );
}